"use client"

import React, { Suspense, useMemo, useCallback, useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Link } from '@/i18n/navigation'
import { HeroSection } from "@/types/blocks/hero"
import { getHeroVideo, DEFAULT_VIDEO_URL } from "@/config/homepage-videos"
import { videoPreloadManager } from "@/utils/videoPerformance"
import OptimizedVideo from '@/components/veo3/OptimizedVideo';

interface HeroOptimizedProps {
  section?: HeroSection;
}

// 优化的CTA按钮组件
const OptimizedCTAButton: React.FC = React.memo(() => {
  return (
    <Button
      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm lg:text-base rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto max-w-xs sm:max-w-none"
      asChild
      aria-label="Animate with Wan22 AI"
    >
      <Link href="/wan22">
        <span className="flex items-center justify-center gap-1.5 sm:gap-2">
          <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
          <span className="whitespace-nowrap">Animate with Wan22 AI</span>
        </span>
      </Link>
    </Button>
  )
});

OptimizedCTAButton.displayName = 'OptimizedCTAButton';

// 背景装饰组件
const BackgroundDecoration: React.FC = React.memo(() => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: -1 }}>
      {/* 渐变背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />

      {/* 网格背景 */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            linear-gradient(to right, rgba(16,185,129,0.2) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(16,185,129,0.2) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          opacity: 0.1,
          zIndex: 1
        }}
      />
    </div>
  )
});

BackgroundDecoration.displayName = 'BackgroundDecoration';

// 主Hero组件
const HeroOptimized: React.FC<HeroOptimizedProps> = ({ section }) => {
  // 如果区块被禁用，返回 null
  if (section?.disabled) {
    return null;
  }

  // 从配置文件获取视频配置
  const heroVideoConfig = getHeroVideo();
  
  // 视频透明度 - 优先使用配置文件中的设置
  const videoOpacityClass = useMemo(() => {
    const opacity = heroVideoConfig.opacity || section?.video?.opacity || 90;
    return opacity <= 60 ? 'opacity-60' : 'opacity-90';
  }, [heroVideoConfig.opacity, section?.video?.opacity]);

  // 视频URL - 优先使用配置文件中的URL
  const videoUrl = heroVideoConfig.url || section?.video?.url || DEFAULT_VIDEO_URL;

  return (
    <section className="relative min-h-screen bg-black overflow-hidden" style={{ zIndex: 0 }}>
      {/* 背景装饰 */}
      <BackgroundDecoration />

      {/* 内容容器 */}
      <div className="relative z-0 flex flex-col lg:flex-row items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 pt-0 pb-4 sm:pt-0 sm:pb-8 lg:py-16">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8 lg:gap-16">
            
            {/* 上方文字内容 */}
            <div className="flex-1 w-full max-w-lg sm:max-w-xl lg:max-w-2xl order-1 lg:order-1">
              <div className="text-center lg:text-left">
                {/* 标题 */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 lg:mb-8 leading-tight">
                  {section?.title ? (
                    <>
                      <span className="block text-white mb-1 sm:mb-2">
                        {section.title.part1}
                      </span>
                      <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                        {section.title.part2}
                      </span>
                    </>
                  ) : (
                    <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                      AI 视频生成
                    </span>
                  )}
                </h1>
                
                {/* 描述 */}
                <p className="text-base sm:text-lg text-gray-200 mb-6 sm:mb-8 lg:mb-12 leading-relaxed">
                  {section?.description || "从文本描述、图片或视频开始，我们的AI将为您创建专业级的视频内容，支持多种风格和效果。"}
                </p>
                
                {/* CTA按钮 */}
                <div className="flex justify-center lg:justify-start">
                  <OptimizedCTAButton />
                </div>
              </div>
            </div>

            {/* 下方视频展示 */}
            <div className="flex-1 w-full max-w-lg sm:max-w-xl lg:max-w-2xl order-2 lg:order-2 mb-2 sm:mb-4 lg:mb-0">
              <div className="relative w-full aspect-[4/3] sm:aspect-video rounded-lg sm:rounded-xl lg:rounded-2xl overflow-hidden bg-gray-800 shadow-2xl">
                {videoUrl ? (
                  <Suspense fallback={
                    <div className="absolute inset-0 bg-gray-800 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 border-4 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                    </div>
                  }>
                    <OptimizedVideo
                      src={videoUrl}
                      className={`w-full h-full object-cover rounded-lg sm:rounded-xl lg:rounded-2xl ${videoOpacityClass}`}
                    />
                  </Suspense>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-gray-400 px-4">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 mx-auto mb-2 sm:mb-3 lg:mb-4 bg-blue-500/20 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center">
                        <svg className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-sm sm:text-base lg:text-lg font-semibold mb-1 sm:mb-2">AI 视频预览</p>
                      <p className="text-xs sm:text-sm text-gray-200">即将开始</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroOptimized; 